import { useState, useMemo, useRef, useEffect, useCallback } from "react";
import { useForm } from "react-hook-form";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { getRegions, getDistricts } from "@/lib/tanzaniaLocations";
import { getSignedFileUrl } from "@/lib/getSignedFileUrl";
import { Camera, Upload, Fingerprint, PenTool, Trash2, Loader2, RefreshCw, ExternalLink } from "lucide-react";
import { captureFingerprint, bmpBase64ToPngBlob } from "@/lib/secugenWebAPI";
import type { Tables } from "@/integrations/supabase/types";
import { useAuth } from "@/contexts/AuthContext";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";

function applyBlueBackground(blob: Blob): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext("2d");
      if (!ctx) return reject(new Error("Canvas not supported"));
      ctx.drawImage(img, 0, 0);
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const data = imageData.data;
      const threshold = 200;
      for (let i = 0; i < data.length; i += 4) {
        if (data[i] > threshold && data[i + 1] > threshold && data[i + 2] > threshold) {
          data[i] = 29;      // R
          data[i + 1] = 78;  // G
          data[i + 2] = 216; // B  (#1d4ed8)
        }
      }
      ctx.putImageData(imageData, 0, 0);
      canvas.toBlob((result) => {
        if (result) resolve(result);
        else reject(new Error("Failed to create blob"));
      }, "image/png");
    };
    img.onerror = () => reject(new Error("Failed to load image"));
    img.src = URL.createObjectURL(blob);
  });
}

type Student = Tables<"students">;

interface StudentFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  student?: Student | null;
  onSuccess: () => void;
}

// Manual override statuses only. The auto-managed ones
// (unregistered_inactive, unregistered_active, registered_active) are set by
// signup flow + DB triggers + Accounting clearance, so they are not selectable.
const STATUS_OPTIONS = [
  { value: "registered_inactive", label: "Registered Inactive" },
  { value: "postponed", label: "Postponed" },
  { value: "suspended", label: "Suspended" },
  { value: "discontinued", label: "Discontinued" },
  { value: "deceased", label: "Deceased" },
  { value: "terminated", label: "Terminated" },
];

const AUTO_STATUSES = ["unregistered_inactive", "unregistered_active", "registered_active"];

function formatStatusLabel(s: string) {
  return s.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
}

const SIT_STATUS_OPTIONS = [
  { value: "first_sit", label: "First Sit" },
  { value: "repeating_module", label: "Repeat Module" },
  { value: "repeat_semester", label: "Repeat Semester" },
  { value: "supplementary", label: "Supplementary" },
  { value: "incomplete", label: "Incomplete" },
];

function parseAddress(address: string | null): { region: string; district: string; area: string } {
  if (!address) return { region: "", district: "", area: "" };
  const parts = address.split(", ");
  if (parts.length >= 3) {
    return { area: parts.slice(0, -2).join(", "), district: parts[parts.length - 2], region: parts[parts.length - 1] };
  }
  return { region: "", district: "", area: address };
}

export function StudentForm({ open, onOpenChange, student, onSuccess }: StudentFormProps) {
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);
  const isEditing = !!student;
  const parsed = useMemo(() => parseAddress(student?.address || null), [student?.address]);

  const [region, setRegion] = useState(parsed.region);
  const [district, setDistrict] = useState(parsed.district);

  const regions = getRegions();
  const districts = region ? getDistricts(region) : [];

  const [noNida, setNoNida] = useState(false);

  const { register, handleSubmit, setValue, watch, reset } = useForm({
    defaultValues: {
      gender: student?.gender || "",
      date_of_birth: student?.date_of_birth || "",
      status: student?.status || "unregistered_inactive",
      sit_status: student?.sit_status || "first_sit",
      current_semester: (student as any)?.current_semester || "",
      enrollment_date: student?.enrollment_date || new Date().toISOString().split("T")[0],
      area: parsed.area,
      nida_number: (student as any)?.nida_number || "",
    },
  });

  // Reset form when student changes
  useEffect(() => {
    if (open) {
      const p = parseAddress(student?.address || null);
      const nidaVal = (student as any)?.nida_number || "";
      reset({
        gender: student?.gender || "",
        date_of_birth: student?.date_of_birth || "",
        status: student?.status || "unregistered_inactive",
        sit_status: student?.sit_status || "first_sit",
        current_semester: (student as any)?.current_semester || "",
        enrollment_date: student?.enrollment_date || new Date().toISOString().split("T")[0],
        area: p.area,
        nida_number: nidaVal,
      });
      setRegion(p.region);
      setDistrict(p.district);
      setNoNida(nidaVal === "no_nida");
      // Auto-navigate to step 2 if step 1 data already saved
      const hasStep1 = student?.gender && student?.date_of_birth && student?.enrollment_date;
      setStep(hasStep1 ? 2 : 1);
    }
  }, [open, student, reset]);

  const onSubmit = async (raw: any, goNext = false) => {
    setLoading(true);
    const addressParts = [raw.area, district, region].filter(Boolean);
    const address = addressParts.length > 0 ? addressParts.join(", ") : null;

    const updateData: Record<string, any> = {
      gender: raw.gender || null,
      date_of_birth: raw.date_of_birth || null,
      status: raw.status,
      sit_status: raw.sit_status || null,
      current_semester: raw.current_semester || null,
      enrollment_date: raw.enrollment_date || null,
      address,
      nida_number: noNida ? "no_nida" : (raw.nida_number || null),
    };

    try {
      if (isEditing) {
        const { error } = await supabase.from("students").update(updateData as any).eq("id", student.id);
        if (error) throw error;
        toast({ title: "Student details saved successfully" });
      } else {
        toast({ title: "Error", description: "Cannot create student from this form", variant: "destructive" });
        setLoading(false);
        return;
      }

      if (goNext) {
        setStep(2);
      } else {
        reset();
        setRegion("");
        setDistrict("");
        onOpenChange(false);
        onSuccess();
      }
    } catch (error: any) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setStep(1);
    onOpenChange(false);
    onSuccess();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {step === 1 ? "Add Student Details" : "Biometric & Digital Details"}
          </DialogTitle>
        </DialogHeader>

        {student && (
          <div className="rounded-md bg-muted p-3 text-sm space-y-1">
            <p><span className="font-medium">Name:</span> {student.first_name} {student.last_name}</p>
            <p><span className="font-medium">Student #:</span> {student.student_number}</p>
            {student.class && <p><span className="font-medium">Class:</span> {student.class}</p>}
          </div>
        )}

        {step === 1 ? (
          <form onSubmit={handleSubmit((data) => onSubmit(data, false))} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Gender</Label>
                <Select value={watch("gender")} onValueChange={(v) => setValue("gender", v)}>
                  <SelectTrigger><SelectValue placeholder="Select gender" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="male">Male</SelectItem>
                    <SelectItem value="female">Female</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="date_of_birth">Date of Birth</Label>
                <Input id="date_of_birth" type="date" {...register("date_of_birth")} />
              </div>
              {student && (
                <div className="space-y-2">
                  <Label>Status</Label>
                  {AUTO_STATUSES.includes(watch("status")) ? (
                    <div className="space-y-1.5">
                      <div className="flex items-center gap-2 rounded-md border bg-muted/40 px-3 py-2">
                        <span className="text-sm font-medium">{formatStatusLabel(watch("status"))}</span>
                        <span className="text-[10px] uppercase tracking-wide text-muted-foreground">System-managed</span>
                      </div>
                      <Select value="" onValueChange={(v) => setValue("status", v)}>
                        <SelectTrigger><SelectValue placeholder="Override status…" /></SelectTrigger>
                        <SelectContent>
                          {STATUS_OPTIONS.map((opt) => (
                            <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  ) : (
                    <Select value={watch("status")} onValueChange={(v) => setValue("status", v)}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {STATUS_OPTIONS.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>
              )}
              <div className="space-y-2">
                <Label>Sit Status</Label>
                <Select value={watch("sit_status")} onValueChange={(v) => setValue("sit_status", v)}>
                  <SelectTrigger><SelectValue placeholder="Select sit status" /></SelectTrigger>
                  <SelectContent>
                    {SIT_STATUS_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Current Semester</Label>
                <Select value={watch("current_semester")} onValueChange={(v) => setValue("current_semester", v)}>
                  <SelectTrigger><SelectValue placeholder="Select semester" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Semester I">Semester I</SelectItem>
                    <SelectItem value="Semester II">Semester II</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2 col-span-2">
                <Label htmlFor="enrollment_date">Registration Date</Label>
                <Input id="enrollment_date" type="date" {...register("enrollment_date")} />
              </div>
              <div className="space-y-2 col-span-2">
                <Label htmlFor="nida_number">NIDA Number</Label>
                <div className="flex items-center gap-2">
                  <Input
                    id="nida_number"
                    {...register("nida_number")}
                    placeholder="e.g. 19900101-12345-12345-01"
                    disabled={noNida}
                    className="flex-1"
                  />
                  <label className="flex items-center gap-1.5 text-xs text-muted-foreground whitespace-nowrap cursor-pointer">
                    <input
                      type="checkbox"
                      checked={noNida}
                      onChange={(e) => {
                        setNoNida(e.target.checked);
                        if (e.target.checked) setValue("nida_number", "");
                      }}
                      className="accent-primary"
                    />
                    No NIDA
                  </label>
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <Label className="text-base font-medium">Address</Label>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Region</Label>
                  <Select value={region} onValueChange={(v) => { setRegion(v); setDistrict(""); }}>
                    <SelectTrigger><SelectValue placeholder="Select region" /></SelectTrigger>
                    <SelectContent>
                      {regions.map((r) => (
                        <SelectItem key={r} value={r}>{r}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>District</Label>
                  <Select value={district} onValueChange={setDistrict} disabled={!region}>
                    <SelectTrigger><SelectValue placeholder="Select district" /></SelectTrigger>
                    <SelectContent>
                      {districts.map((d) => (
                        <SelectItem key={d} value={d}>{d}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="area">Area / Street / Village</Label>
                <Input id="area" {...register("area")} placeholder="e.g. Kijitonyama, Block 45" />
              </div>
            </div>

            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={handleClose}>Cancel</Button>
              <Button type="submit" disabled={loading}>{loading ? "Saving..." : "Save"}</Button>
              <Button
                type="button"
                disabled={loading}
                onClick={handleSubmit((data) => onSubmit(data, true))}
              >
                {loading ? "Saving..." : "Save & Next"}
              </Button>
            </DialogFooter>
          </form>
        ) : (
          <BiometricsStep studentId={student?.id || ""} onClose={handleClose} onBackToStep1={() => setStep(1)} />
        )}
      </DialogContent>
    </Dialog>
  );
}

/* ─── Step 2: Biometrics ─── */

function BiometricsStep({ studentId, onClose, onBackToStep1 }: { studentId: string; onClose: () => void; onBackToStep1: () => void }) {
  const [uploading, setUploading] = useState<string | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [signaturePreview, setSignaturePreview] = useState<string | null>(null);
  const [fingerprintPreview, setFingerprintPreview] = useState<string | null>(null);
  const [webcamActive, setWebcamActive] = useState(false);
  const [capturedBlob, setCapturedBlob] = useState<Blob | null>(null);
  const [capturedPreviewUrl, setCapturedPreviewUrl] = useState<string | null>(null);
  const [signaturePadActive, setSignaturePadActive] = useState(false);
  const [fpScanning, setFpScanning] = useState(false);
  const [fpCapturedBlob, setFpCapturedBlob] = useState<Blob | null>(null);
  const [fpCapturedPreviewUrl, setFpCapturedPreviewUrl] = useState<string | null>(null);
  const [videoDevices, setVideoDevices] = useState<MediaDeviceInfo[]>([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState<string>("");
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const sigCanvasRef = useRef<HTMLCanvasElement>(null);
  const sigDrawing = useRef(false);

  const [biometricsLocked, setBiometricsLocked] = useState(false);
  const [resetting, setResetting] = useState(false);
  const { hasRole } = useAuth();
  const isSuperadmin = hasRole("superadmin");
  const isHeadAdmin = hasRole("head_administration");
  // Both Superadmin and Head of Administration may upload/re-upload at any time.
  const canEdit = isSuperadmin || isHeadAdmin;

  const resetBiometrics = async () => {
    if (!studentId) return;
    setResetting(true);
    try {
      // Fetch existing paths to remove from storage
      const { data: existing } = await supabase
        .from("student_biometrics")
        .select("photo_path, fingerprint_path")
        .eq("student_id", studentId)
        .maybeSingle();

      const pathsToRemove = [existing?.photo_path, existing?.fingerprint_path].filter(Boolean) as string[];
      if (pathsToRemove.length > 0) {
        await supabase.storage.from("academic-files").remove(pathsToRemove);
      }

      const { error } = await supabase
        .from("student_biometrics")
        .update({ photo_path: null, fingerprint_path: null })
        .eq("student_id", studentId);
      if (error) throw error;

      setPhotoPreview(null);
      setFingerprintPreview(null);
      setCapturedBlob(null);
      setCapturedPreviewUrl(null);
      setFpCapturedBlob(null);
      setFpCapturedPreviewUrl(null);
      setBiometricsLocked(false);
      toast({ title: "Biometrics reset", description: "Photo and fingerprint cleared. Re-upload now available." });
    } catch (err: any) {
      toast({ title: "Reset failed", description: err.message, variant: "destructive" });
    } finally {
      setResetting(false);
    }
  };


  // Load existing biometrics
  useEffect(() => {
    if (!studentId) return;
    (async () => {
      const { data } = await supabase
        .from("student_biometrics")
        .select("*")
        .eq("student_id", studentId)
        .maybeSingle();
      if (data) {
        // Lock editing if both photo and fingerprint are captured
        if (data.photo_path && data.fingerprint_path) {
          setBiometricsLocked(true);
        }
        if (data.photo_path) {
          const url = await getSignedFileUrl(data.photo_path);
          if (url) setPhotoPreview(url);
        }
        if (data.signature_path) {
          const url = await getSignedFileUrl(data.signature_path);
          if (url) setSignaturePreview(url);
        }
        if (data.fingerprint_path) {
          const url = await getSignedFileUrl(data.fingerprint_path);
          if (url) setFingerprintPreview(url);
        }
      }
    })();
  }, [studentId]);

  const uploadFile = async (file: File | Blob, field: "photo_path" | "fingerprint_path" | "signature_path", fileName: string) => {
    if (!studentId) {
      toast({ title: "Save the student first", description: "Create the student record before uploading biometrics.", variant: "destructive" });
      return;
    }
    setUploading(field);
    try {
      // Verify session — RLS uploads silently fail without auth
      const { data: sessionData } = await supabase.auth.getSession();
      if (!sessionData.session) {
        throw new Error("Your session has expired. Please log in again.");
      }

      const path = `students/${studentId}/${fileName}`;
      const { error: uploadErr } = await supabase.storage
        .from("academic-files")
        .upload(path, file, {
          upsert: true,
          contentType: file.type || "image/png",
          cacheControl: "3600",
        });
      if (uploadErr) {
        console.error("[biometrics upload] storage error", uploadErr);
        throw new Error(uploadErr.message || "Storage upload failed");
      }

      // Explicit select-then-update/insert (avoids upsert schema-cache issue)
      const { data: existingRow, error: selErr } = await supabase
        .from("student_biometrics")
        .select("id")
        .eq("student_id", studentId)
        .maybeSingle();
      if (selErr) {
        console.error("[biometrics upload] select error", selErr);
        throw new Error(selErr.message || "Could not check existing biometrics");
      }

      if (existingRow?.id) {
        const { error: updErr } = await supabase
          .from("student_biometrics")
          .update({ [field]: path } as any)
          .eq("id", existingRow.id);
        if (updErr) {
          console.error("[biometrics upload] update error", updErr);
          throw new Error(updErr.message || "Database update failed");
        }
      } else {
        const insertPayload: Record<string, any> = { student_id: studentId, [field]: path };
        const { error: insErr } = await supabase
          .from("student_biometrics")
          .insert(insertPayload as any);
        if (insErr) {
          console.error("[biometrics upload] insert error", insErr);
          throw new Error(insErr.message || "Database insert failed");
        }
      }

      // Cache-bust the signed URL preview so replaced files appear fresh
      const signed = await getSignedFileUrl(path);
      const url = signed ? `${signed}${signed.includes("?") ? "&" : "?"}v=${Date.now()}` : null;
      if (field === "photo_path") setPhotoPreview(url);
      if (field === "signature_path") setSignaturePreview(url);
      if (field === "fingerprint_path") setFingerprintPreview(url);

      toast({ title: "Uploaded successfully" });

      // Check if status was auto-promoted to unregistered_active by DB trigger
      if (field === "photo_path" || field === "fingerprint_path") {
        const { data: stu } = await supabase
          .from("students")
          .select("status")
          .eq("id", studentId)
          .maybeSingle();
        if (stu?.status === "unregistered_active") {
          toast({
            title: "Status updated",
            description: "Student promoted to Unregistered Active (biometrics complete).",
          });
        }
      }
    } catch (err: any) {
      console.error("[biometrics upload] failed", err);
      toast({ title: "Upload failed", description: err?.message ?? "Unknown error", variant: "destructive" });
    } finally {
      setUploading(null);
    }
  };

  const streamRef = useRef<MediaStream | null>(null);
  const [activeStream, setActiveStream] = useState<MediaStream | null>(null);
  const [activeCameraLabel, setActiveCameraLabel] = useState<string>("");
  const [isEmbedded, setIsEmbedded] = useState(false);

  const PREFERRED_CAMERA_KEY = "preferred_camera_deviceId";

  // Detect embedded iframe context
  useEffect(() => {
    try {
      setIsEmbedded(window.self !== window.top);
    } catch {
      setIsEmbedded(true);
    }
  }, []);

  // Attach stream to video element whenever stream or webcamActive changes
  useEffect(() => {
    if (webcamActive && videoRef.current && activeStream) {
      videoRef.current.srcObject = activeStream;
      videoRef.current.play().catch(() => {});
    }
  }, [webcamActive, activeStream]);

  // Cleanup streams on unmount / dialog close
  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(t => t.stop());
        streamRef.current = null;
      }
    };
  }, []);

  // Listen for device changes (plug/unplug cameras)
  useEffect(() => {
    if (!navigator.mediaDevices) return;
    const handleDeviceChange = async () => {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const videos = devices.filter(d => d.kind === "videoinput");
      setVideoDevices(videos);
    };
    navigator.mediaDevices.addEventListener("devicechange", handleDeviceChange);
    return () => {
      navigator.mediaDevices.removeEventListener("devicechange", handleDeviceChange);
    };
  }, []);

  // Score a camera device — higher = more likely external
  const scoreDevice = (device: MediaDeviceInfo): number => {
    const label = (device.label || "").toLowerCase();
    let score = 0;
    // Positive signals for external cameras
    if (label.includes("usb")) score += 10;
    if (label.includes("external")) score += 10;
    if (label.includes("webcam") && !label.includes("integrated")) score += 5;
    if (label.includes("hd")) score += 2;
    if (label.includes("logitech") || label.includes("razer") || label.includes("elgato")) score += 8;
    // Negative signals for built-in cameras
    if (label.includes("integrated") || label.includes("built-in") || label.includes("builtin")) score -= 10;
    if (label.includes("front") || label.includes("facetime") || label.includes("ir camera")) score -= 10;
    if (label.includes("obs") || label.includes("virtual")) score -= 5;
    return score;
  };

  // Pick the best camera from the device list
  const pickPreferredCamera = (devices: MediaDeviceInfo[], savedId?: string | null): MediaDeviceInfo | null => {
    if (devices.length === 0) return null;
    // If saved camera still exists, use it
    if (savedId) {
      const saved = devices.find(d => d.deviceId === savedId);
      if (saved) return saved;
    }
    // Score all devices and pick the highest
    const scored = devices.map(d => ({ device: d, score: scoreDevice(d) }));
    scored.sort((a, b) => b.score - a.score);
    // If the top score > 0 it's likely external; otherwise fall back to last device
    if (scored[0].score > 0) return scored[0].device;
    // Default: last device (external USB cameras often appear last)
    return devices[devices.length - 1];
  };

  // Enumerate available cameras
  const enumerateCameras = async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const videos = devices.filter(d => d.kind === "videoinput");
      setVideoDevices(videos);
      return videos;
    } catch {
      return [];
    }
  };

  // Refresh camera list manually
  const refreshCameraList = async () => {
    const videos = await enumerateCameras();
    toast({ title: `Found ${videos.length} camera${videos.length !== 1 ? "s" : ""}` });
  };

  // Attach a stream to the video element directly (works even when webcamActive is already true)
  const attachStream = (stream: MediaStream) => {
    streamRef.current = stream;
    setActiveStream(stream);
    const track = stream.getVideoTracks()[0];
    setActiveCameraLabel(track?.label || "Unknown camera");
    const tryAttach = () => {
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play().catch(() => {});
      }
    };
    tryAttach();
    // Retry on next frame if video element wasn't ready
    requestAnimationFrame(tryAttach);
  };

  // Start webcam with a specific device (or default)
  const startWebcamWithDevice = async (deviceId?: string) => {
    // Stop any existing stream first
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }

    const constraints: MediaStreamConstraints = {
      video: deviceId ? { deviceId: { exact: deviceId } } : true,
    };

    try {
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      attachStream(stream);
      setWebcamActive(true);
      // Save this as the preferred camera
      const activeTrack = stream.getVideoTracks()[0];
      const activeId = activeTrack?.getSettings()?.deviceId;
      if (activeId) {
        setSelectedDeviceId(activeId);
        try { localStorage.setItem(PREFERRED_CAMERA_KEY, activeId); } catch {}
      }
    } catch (err: any) {
      let description = "Could not access the camera.";
      if (err.name === "NotAllowedError") {
        description = "Camera permission denied. Please allow camera access in your browser settings.";
      } else if (err.name === "NotFoundError") {
        description = "No camera found on this device.";
      } else if (err.name === "NotReadableError") {
        description = "Camera is in use by another application.";
      } else if (err.name === "OverconstrainedError") {
        description = "Selected camera is no longer available. Falling back to default.";
        // Retry without specific device
        if (deviceId) {
          try { localStorage.removeItem(PREFERRED_CAMERA_KEY); } catch {}
          return startWebcamWithDevice();
        }
      } else if (err.name === "SecurityError") {
        description = "Camera access blocked by browser security. Try opening the app in a new tab.";
      }
      toast({ title: "Camera unavailable", description, variant: "destructive" });
    }
  };

  // Webcam — getUserMedia called FIRST in click handler for gesture context
  const startWebcam = async () => {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      toast({
        title: "Camera unavailable",
        description: "Camera access is not supported in this browser context. Try opening the app in a new tab.",
        variant: "destructive",
      });
      return;
    }

    // Check if we have a saved preferred camera
    let savedDeviceId: string | null = null;
    try { savedDeviceId = localStorage.getItem(PREFERRED_CAMERA_KEY); } catch {}

    // If we have a saved preferred camera, try it directly in gesture context
    if (savedDeviceId) {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { deviceId: { exact: savedDeviceId } },
        });
        attachStream(stream);
        setWebcamActive(true);
        setSelectedDeviceId(savedDeviceId);
        // Enumerate cameras in background
        enumerateCameras();
        return;
      } catch {
        // Saved camera not available — fall through to default
        try { localStorage.removeItem(PREFERRED_CAMERA_KEY); } catch {}
      }
    }

    // Call getUserMedia IMMEDIATELY — do NOT await anything before this
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      attachStream(stream);
      setWebcamActive(true);

      // Now enumerate cameras (labels available after permission granted)
      const videos = await enumerateCameras();
      const activeTrack = stream.getVideoTracks()[0];
      const activeDeviceId = activeTrack?.getSettings()?.deviceId || "";
      setSelectedDeviceId(activeDeviceId);

      // If multiple cameras, auto-switch to the best one (scored)
      if (videos.length > 1) {
        const best = pickPreferredCamera(videos, null);
        if (best && best.deviceId !== activeDeviceId) {
          setSelectedDeviceId(best.deviceId);
          await startWebcamWithDevice(best.deviceId);
        } else {
          // Current camera is the best — save it
          try { localStorage.setItem(PREFERRED_CAMERA_KEY, activeDeviceId); } catch {}
        }
      } else if (videos.length === 1) {
        try { localStorage.setItem(PREFERRED_CAMERA_KEY, activeDeviceId); } catch {}
      }
    } catch (err: any) {
      let description = "Could not access the camera.";
      if (err.name === "NotAllowedError") {
        description = "Camera permission denied. Please allow camera access in your browser settings.";
      } else if (err.name === "NotFoundError") {
        description = "No camera found on this device.";
      } else if (err.name === "NotReadableError") {
        description = "Camera is in use by another application.";
      } else if (err.name === "SecurityError") {
        description = "Camera access blocked by browser security. Try opening the app in a new tab.";
      }
      toast({ title: "Camera unavailable", description, variant: "destructive" });
    }
  };

  // Switch camera when user changes the dropdown
  const handleCameraChange = (deviceId: string) => {
    setSelectedDeviceId(deviceId);
    startWebcamWithDevice(deviceId);
  };

  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;
    const canvas = canvasRef.current;
    canvas.width = videoRef.current.videoWidth;
    canvas.height = videoRef.current.videoHeight;
    canvas.getContext("2d")?.drawImage(videoRef.current, 0, 0);
    canvas.toBlob(async (rawBlob) => {
      if (rawBlob) {
        try {
          const processed = await applyBlueBackground(rawBlob);
          const url = URL.createObjectURL(processed);
          setCapturedBlob(processed);
          setCapturedPreviewUrl(url);
        } catch {
          // Fallback to raw blob if processing fails
          const url = URL.createObjectURL(rawBlob);
          setCapturedBlob(rawBlob);
          setCapturedPreviewUrl(url);
        }
      }
    }, "image/png");
    stopWebcam();
  };

  // Track URLs transferred to photoPreview so cleanup doesn't revoke them
  const transferredUrlRef = useRef<string | null>(null);

  const confirmCapture = () => {
    if (capturedBlob && capturedPreviewUrl) {
      // Transfer the object URL to photoPreview — don't revoke it
      transferredUrlRef.current = capturedPreviewUrl;
      setPhotoPreview(capturedPreviewUrl);
      uploadFile(capturedBlob, "photo_path", "passport_photo.png");
    } else if (capturedPreviewUrl) {
      URL.revokeObjectURL(capturedPreviewUrl);
    }
    setCapturedBlob(null);
    setCapturedPreviewUrl(null);
  };

  const retakePhoto = () => {
    if (capturedPreviewUrl) URL.revokeObjectURL(capturedPreviewUrl);
    setCapturedBlob(null);
    setCapturedPreviewUrl(null);
    startWebcam();
  };

  // Cleanup preview URL on unmount — skip if transferred to photoPreview
  useEffect(() => {
    return () => {
      if (capturedPreviewUrl && capturedPreviewUrl !== transferredUrlRef.current) {
        URL.revokeObjectURL(capturedPreviewUrl);
      }
    };
  }, [capturedPreviewUrl]);

  const stopWebcam = () => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
    setWebcamActive(false);
  };

  // SecuGen fingerprint scanner capture — always attempt directly
  const scanFingerprint = async () => {
    setFpScanning(true);
    try {
      const result = await captureFingerprint({ timeout: 10000, quality: 50 });
      const pngBlob = await bmpBase64ToPngBlob(result.BMPBase64);
      const url = URL.createObjectURL(pngBlob);
      setFpCapturedBlob(pngBlob);
      setFpCapturedPreviewUrl(url);
      toast({ title: `Fingerprint captured (Quality: ${result.ImageQuality}, NFIQ: ${result.NFIQ})` });
    } catch (err: any) {
      toast({
        title: "Fingerprint capture failed",
        description: `${err.message}. Ensure SgiBioSrv is running, the scanner is plugged in, and you have trusted the certificate at https://localhost:8443`,
        variant: "destructive",
      });
    } finally {
      setFpScanning(false);
    }
  };

  const fpTransferredUrlRef = useRef<string | null>(null);

  const confirmFpCapture = () => {
    if (fpCapturedBlob && fpCapturedPreviewUrl) {
      fpTransferredUrlRef.current = fpCapturedPreviewUrl;
      setFingerprintPreview(fpCapturedPreviewUrl);
      uploadFile(fpCapturedBlob, "fingerprint_path", "fingerprint.png");
    }
    setFpCapturedBlob(null);
    setFpCapturedPreviewUrl(null);
  };

  const retakeFp = () => {
    if (fpCapturedPreviewUrl) URL.revokeObjectURL(fpCapturedPreviewUrl);
    setFpCapturedBlob(null);
    setFpCapturedPreviewUrl(null);
    scanFingerprint();
  };

  useEffect(() => {
    return () => {
      if (fpCapturedPreviewUrl && fpCapturedPreviewUrl !== fpTransferredUrlRef.current) {
        URL.revokeObjectURL(fpCapturedPreviewUrl);
      }
    };
  }, [fpCapturedPreviewUrl]);

  // Signature pad
  const initSignaturePad = useCallback(() => {
    const canvas = sigCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = "#000000";
    ctx.lineWidth = 2;
    ctx.lineCap = "round";

    const getPos = (e: MouseEvent | TouchEvent) => {
      const rect = canvas.getBoundingClientRect();
      if ("touches" in e) {
        return { x: e.touches[0].clientX - rect.left, y: e.touches[0].clientY - rect.top };
      }
      return { x: (e as MouseEvent).clientX - rect.left, y: (e as MouseEvent).clientY - rect.top };
    };

    const onDown = (e: MouseEvent | TouchEvent) => {
      e.preventDefault();
      sigDrawing.current = true;
      const pos = getPos(e);
      ctx.beginPath();
      ctx.moveTo(pos.x, pos.y);
    };
    const onMove = (e: MouseEvent | TouchEvent) => {
      if (!sigDrawing.current) return;
      e.preventDefault();
      const pos = getPos(e);
      ctx.lineTo(pos.x, pos.y);
      ctx.stroke();
    };
    const onUp = () => { sigDrawing.current = false; };

    canvas.addEventListener("mousedown", onDown);
    canvas.addEventListener("mousemove", onMove);
    canvas.addEventListener("mouseup", onUp);
    canvas.addEventListener("mouseleave", onUp);
    canvas.addEventListener("touchstart", onDown, { passive: false });
    canvas.addEventListener("touchmove", onMove, { passive: false });
    canvas.addEventListener("touchend", onUp);
  }, []);

  useEffect(() => {
    if (signaturePadActive) {
      setTimeout(initSignaturePad, 100);
    }
  }, [signaturePadActive, initSignaturePad]);

  const saveSignature = () => {
    const canvas = sigCanvasRef.current;
    if (!canvas) return;
    canvas.toBlob((blob) => {
      if (blob) uploadFile(blob, "signature_path", "e_signature.png");
    }, "image/png");
    setSignaturePadActive(false);
  };

  const clearSignaturePad = () => {
    const canvas = sigCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  };

  return (
    <div className="space-y-6">
      {biometricsLocked && (
        <div className="rounded-md border border-blue-500/50 bg-blue-50 dark:bg-blue-900/20 p-3 text-sm text-blue-800 dark:text-blue-200 flex items-start justify-between gap-3">
          <span>
            Biometrics already captured. {canEdit
              ? "You can re-upload to replace any item below."
              : "Only Superadmin or Head of Administration can replace these items."}
          </span>
          {isSuperadmin && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" size="sm" className="shrink-0 border-destructive/50 text-destructive hover:bg-destructive/10" disabled={resetting}>
                  {resetting ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Trash2 className="h-3.5 w-3.5 mr-1" />}
                  Clear all
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Clear biometrics?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This will permanently delete the student's captured photo and fingerprint. This action cannot be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction className="bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={resetBiometrics}>
                    Delete & Unlock
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      )}


      {/* Passport Photo */}
      <div className="space-y-2">
        <Label className="text-base font-medium">Passport Photo</Label>
        {photoPreview && (
          <div className="w-[140px] h-[180px] rounded-md overflow-hidden bg-blue-700 flex items-center justify-center border">
            <img src={photoPreview} alt="Passport" className="w-full h-full object-cover" />
          </div>
        )}
        {canEdit && (
          <div className="flex gap-2 flex-wrap">
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={!!uploading}
              onClick={() => document.getElementById("photo-upload")?.click()}
            >
              <Upload className="mr-1 h-4 w-4" /> {photoPreview ? "Replace Photo" : "Upload Photo"}
            </Button>
            <input
              id="photo-upload"
              type="file"
              accept="image/*"
              className="hidden"
              onChange={async (e) => {
                const f = e.target.files?.[0];
                e.target.value = ""; // allow re-selecting the same file
                if (f) {
                  try {
                    const processed = await applyBlueBackground(f);
                    uploadFile(processed, "photo_path", "passport_photo.png");
                  } catch {
                    uploadFile(f, "photo_path", `passport_photo.${f.name.split(".").pop()}`);
                  }
                }
              }}
            />
            {!webcamActive ? (
              <Button type="button" variant="outline" size="sm" onClick={startWebcam} disabled={!!uploading}>
                <Camera className="mr-1 h-4 w-4" /> Use Webcam
              </Button>
            ) : (
              <Button type="button" variant="outline" size="sm" onClick={stopWebcam}>
                Cancel Webcam
              </Button>
            )}
          </div>
        )}
        {isEmbedded && !webcamActive && (
          <div className="rounded-md border border-yellow-500/50 bg-yellow-50 dark:bg-yellow-900/20 p-2 text-xs text-yellow-800 dark:text-yellow-200 flex items-center gap-2">
            <ExternalLink className="h-3.5 w-3.5 shrink-0" />
            <span>
              External camera may not work in preview mode.{" "}
              <a
                href={window.location.href}
                target="_blank"
                rel="noopener noreferrer"
                className="underline font-medium"
              >
                Open in new tab
              </a>{" "}
              for full camera access.
            </span>
          </div>
        )}
        {webcamActive && (
          <div className="space-y-2">
            {activeCameraLabel && (
              <p className="text-xs text-muted-foreground">
                Active: <span className="font-medium">{activeCameraLabel}</span>
              </p>
            )}
            <div className="flex items-center gap-2">
              {videoDevices.length > 0 && (
                <Select value={selectedDeviceId} onValueChange={handleCameraChange}>
                  <SelectTrigger className="max-w-xs">
                    <SelectValue placeholder="Select camera" />
                  </SelectTrigger>
                  <SelectContent>
                    {videoDevices.map((d, i) => (
                      <SelectItem key={d.deviceId} value={d.deviceId}>
                        {d.label || `Camera ${i + 1}`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              <Button type="button" variant="ghost" size="icon" onClick={refreshCameraList} title="Refresh camera list">
                <RefreshCw className="h-4 w-4" />
              </Button>
            </div>
            <video ref={videoRef} className="w-full max-w-xs rounded border" autoPlay playsInline muted />
            <canvas ref={canvasRef} className="hidden" />
            <Button type="button" size="sm" onClick={capturePhoto}>
              <Camera className="mr-1 h-4 w-4" /> Capture
            </Button>
          </div>
        )}
        {capturedPreviewUrl && (
          <div className="space-y-2">
            <p className="text-sm font-medium text-muted-foreground">Captured Passport Photo</p>
            <div className="w-[140px] h-[180px] rounded-md border-2 border-primary/30 overflow-hidden bg-blue-700">
              <img src={capturedPreviewUrl} alt="Captured passport photo" className="w-full h-full object-cover" />
            </div>
            <div className="flex gap-2">
              <Button type="button" variant="outline" size="sm" onClick={retakePhoto}>
                <Camera className="mr-1 h-4 w-4" /> Retake
              </Button>
              <Button type="button" size="sm" onClick={confirmCapture}>
                Confirm & Save
              </Button>
            </div>
          </div>
        )}
        {uploading === "photo_path" && <p className="text-xs text-muted-foreground">Uploading...</p>}
      </div>

      {/* Electronic Signature */}
      <div className="space-y-2">
        <Label className="text-base font-medium">Electronic Signature</Label>
        {signaturePreview && (
          <img src={signaturePreview} alt="Signature" className="h-20 rounded border bg-background" />
        )}
        <div className="flex gap-2 flex-wrap">
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={!!uploading}
            onClick={() => document.getElementById("sig-upload")?.click()}
          >
            <Upload className="mr-1 h-4 w-4" /> Upload Scan
          </Button>
          <input
            id="sig-upload"
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              e.target.value = "";
              if (f) uploadFile(f, "signature_path", `e_signature.${f.name.split(".").pop()}`);
            }}
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setSignaturePadActive(!signaturePadActive)}
            disabled={!!uploading}
          >
            <PenTool className="mr-1 h-4 w-4" /> {signaturePadActive ? "Close Pad" : "Draw Signature"}
          </Button>
        </div>
        {signaturePadActive && (
          <div className="space-y-2">
            <canvas
              ref={sigCanvasRef}
              width={400}
              height={150}
              className="border rounded bg-background w-full max-w-md touch-none"
            />
            <div className="flex gap-2">
              <Button type="button" size="sm" onClick={saveSignature}>Save Signature</Button>
              <Button type="button" size="sm" variant="outline" onClick={clearSignaturePad}>
                <Trash2 className="mr-1 h-4 w-4" /> Clear
              </Button>
            </div>
          </div>
        )}
        {uploading === "signature_path" && <p className="text-xs text-muted-foreground">Uploading...</p>}
      </div>

      {/* Fingerprint */}
      <div className="space-y-3">
        <Label className="text-base font-medium">Fingerprint</Label>

        {fingerprintPreview && !fpCapturedPreviewUrl && (
          <img src={fingerprintPreview} alt="Fingerprint" className="w-24 h-24 rounded border object-cover" />
        )}

        {canEdit && (
          <div className="flex gap-2 flex-wrap">
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={!!uploading}
              onClick={() => document.getElementById("fp-upload")?.click()}
            >
              <Upload className="mr-1 h-4 w-4" /> {fingerprintPreview ? "Replace Scan" : "Upload Scan"}
            </Button>
            <input
              id="fp-upload"
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                e.target.value = "";
                if (f) uploadFile(f, "fingerprint_path", `fingerprint.${f.name.split(".").pop()}`);
              }}
            />
            <Button
              type="button"
              variant="default"
              size="sm"
              onClick={scanFingerprint}
              disabled={!!uploading || fpScanning}
            >
              <Fingerprint className="mr-1 h-4 w-4" /> {fpScanning ? "Scanning..." : "Scan Fingerprint"}
              {fpScanning && <Loader2 className="ml-1 h-4 w-4 animate-spin" />}
            </Button>
          </div>
        )}
        {fpScanning && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Place your finger on the SecuGen scanner...
          </div>
        )}
        {fpCapturedPreviewUrl && (
          <div className="space-y-2">
            <p className="text-sm font-medium text-muted-foreground">Captured Fingerprint</p>
            <div className="w-24 h-24 rounded-md border-2 border-primary/30 overflow-hidden bg-muted">
              <img src={fpCapturedPreviewUrl} alt="Captured fingerprint" className="w-full h-full object-cover" />
            </div>
            <div className="flex gap-2">
              <Button type="button" variant="outline" size="sm" onClick={retakeFp}>
                <Camera className="mr-1 h-4 w-4" /> Retake
              </Button>
              <Button type="button" size="sm" onClick={confirmFpCapture}>
                Confirm & Save
              </Button>
            </div>
          </div>
        )}
        {uploading === "fingerprint_path" && <p className="text-xs text-muted-foreground">Uploading...</p>}
      </div>

      <DialogFooter className="gap-2">
        <Button type="button" variant="outline" size="sm" onClick={onBackToStep1}>Back</Button>
        <Button type="button" variant="outline" onClick={() => onClose()}>Done</Button>
      </DialogFooter>
    </div>
  );
}
